import React, { useState } from 'react';
import {
    useGetModelResultsQuery,
    useDeliverProjectMutation,
    useUpdateStageStatusMutation,
    useUpdatePortalProjectMutation,
} from '../../../redux/api/compassPortal';
import { errMsg } from './bits';

// Delivery — the last stage. Pushes the fitted model's deliverables (results
// CSV + summary) to the Compass MMM Reports Shared Drive as the automation user,
// records the folder link on the project, and signs the stage off. A manual
// link field stays as a fallback for anything delivered out of band.
export default function DeliveryWorkspace({ project, onBack, onComplete }) {
    const pid = project.project_id;
    const [err, setErr] = useState(null);
    const [note, setNote] = useState(null);
    const [folder, setFolder] = useState(project.google_folder_url || '');

    const { data: model } = useGetModelResultsQuery({ projectId: pid });
    const [deliver, { isLoading: delivering }] = useDeliverProjectMutation();
    const [updateStageStatus, { isLoading: completing }] = useUpdateStageStatusMutation();
    const [updateProject, { isLoading: savingFolder }] = useUpdatePortalProjectMutation();

    const hasModel = model?.status === 'done';
    const link = project.google_folder_url;

    const runDeliver = async () => {
        setErr(null); setNote(null);
        try {
            const r = await deliver({ projectId: pid }).unwrap();
            setNote('Delivered to Compass MMM Reports.');
            if (r?.folder_url) setFolder(r.folder_url);
        } catch (e) { setErr(errMsg(e)); }
    };
    const saveFolder = async () => {
        setErr(null);
        try { await updateProject({ projectId: pid, google_folder_url: folder.trim() }).unwrap(); setNote('Saved.'); }
        catch (e) { setErr(errMsg(e)); }
    };
    const complete = async () => {
        setErr(null);
        try {
            await updateStageStatus({ projectId: pid, stage_id: 'compass-delivery', status: 'complete' }).unwrap();
            onComplete?.('compass-delivery', null);
        } catch (e) { setErr(errMsg(e)); }
    };

    return (
        <>
            <button className="cp-back-btn" onClick={onBack}>← Back to projects</button>
            <div className="cp-toolbar">
                <p className="cp-section-label" style={{ margin: 0 }}>Delivery</p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="cp-btn cp-btn--primary" disabled={!hasModel || delivering} onClick={runDeliver}>
                        {delivering ? 'Delivering…' : project.delivered ? 'Re-deliver to Drive' : 'Deliver to Drive'}
                    </button>
                    {/* completable once there's a delivery folder — auto OR a manually saved link,
                        so the manual fallback isn't a dead end when auto-delivery can't run */}
                    {(project.delivered || project.google_folder_url) && (
                        <button className="cp-btn cp-btn--ghost" disabled={completing} onClick={complete}>
                            {completing ? 'Saving…' : 'Mark stage complete'}
                        </button>
                    )}
                </div>
            </div>
            {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
            {note && <p className="cp-sub" style={{ marginTop: -4, color: 'var(--co-green-dark)' }}>{note}</p>}

            {!hasModel && (
                <div className="cp-empty">Fit a model first — the finished outputs are what gets delivered.</div>
            )}

            {hasModel && (
                <div className="cp-card" style={{ padding: 18, marginBottom: 16 }}>
                    <p className="cp-sub" style={{ marginTop: 0 }}>
                        Delivering pushes the model results (channel contributions, ROI, and the summary) to the
                        <b> Data Portal → {project.client_name} → {project.project_name} → Model Outputs</b> folder
                        (alongside Model Inputs) as the automation user. It's idempotent — re-delivering replaces the files.
                    </p>
                    {link ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
                            <a className="cp-btn cp-btn--primary cp-btn--sm" href={link} target="_blank" rel="noreferrer">
                                Open delivery folder ↗
                            </a>
                            {project.delivered_at && <span className="cp-src-note">delivered{project.delivered_by ? ` by ${project.delivered_by}` : ''}</span>}
                        </div>
                    ) : (
                        <p className="cp-src-note" style={{ marginTop: 6 }}>Not delivered yet.</p>
                    )}
                </div>
            )}

            <div className="cp-card" style={{ padding: 18 }}>
                <p className="cp-section-label" style={{ marginTop: 0 }}>Or set the folder link manually</p>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={folder} onChange={(e) => setFolder(e.target.value)}
                        placeholder="https://drive.google.com/drive/folders/…"
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--co-radius-sm)', border: '1px solid rgba(20,22,21,0.2)', font: 'inherit' }} />
                    <button className="cp-btn cp-btn--ghost cp-btn--sm" disabled={savingFolder || !folder.trim()} onClick={saveFolder}>
                        {savingFolder ? 'Saving…' : 'Save link'}
                    </button>
                </div>
            </div>
        </>
    );
}
